import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import { ArrowRight, Download } from 'lucide-react';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import EngagementAttachments from '../components/shared/EngagementAttachments';
import { FormModal, Field, FilePicker, inputCls, btnGhost, btnPrimary } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import StatusActionsCard from '../components/workflow/StatusActionsCard';
import ChangesSinceReturnCard from '../components/workflow/ChangesSinceReturnCard';
import WorkflowStatusBadge from '../components/workflow/WorkflowStatusBadge';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import { detailLabel } from '../components/workflow/constants';
import AttachmentsList from '../components/workflow/AttachmentsList';
import EditableAttachments, { useRemovableFiles } from '../components/workflow/EditableAttachments';
import HistoryCard from '../components/workflow/HistoryCard';
import RejectModal from '../components/workflow/RejectModal';
import ReturnCommentsBanner from '../components/workflow/ReturnCommentsBanner';
import FieldCommentAnchor from '../components/workflow/FieldCommentAnchor';
import { useFieldComments, FieldCommentsProvider, FieldCommentInline } from '../components/workflow/fieldComments';
import ApprovalsCard from '../components/workflow/ApprovalsCard';
import ReviewerSelect, { useClearOffRosterPick } from '../components/workflow/ReviewerSelect';
import { EngagementName, EngagementNameList, withCoverageFmt } from '../components/workflow/CoverageDisplay';
import BatchSiblings from '../components/workflow/BatchSiblings';
import NowName, { StaffNames } from '../components/shared/NowName';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { engagementHref } from '../utils/engagementLink';
import { archivalDateError, ARCHIVAL_DATE_HINT } from './WorkflowQc35New';
import { Qc35ChecklistForm, Qc35ChecklistView } from '../components/workflow/Qc35Checklist';
import Qc35ScreenshotExample, { QC35_SCREENSHOT_HINT } from '../components/workflow/Qc35ScreenshotExample';
import { qc35AnswerGaps, qc35AnswerLabels, expandQc35AnswerChanges } from './qc35Questions';
import { changesSinceReturn } from '../components/workflow/changeDiff';

// QC35 — detail page (docs/modules/workflow-qc35.md). Approval is direct —
// no signing date prompt, no sign-off stamping; it records the archival date
// onto the covered engagements and marks them archived. The File archival
// checklist IS the QC35 (no uploaded document; the filled form prints as a
// PDF once approved). The tech-tools / restatement / independence fields are
// not on this flow (asked once, on the Signed Financial Statements submission).
const fmtDate = (v) => (v ? formatDate(v) : '—');

// Historic submissions may still carry changes on fields since removed
// — keep their labels so old history rows stay readable.
const CHANGE_LABELS = {
  techToolsUsed: 'Technological Tools Used',
  techToolsAreas: 'Tool Deployment Areas',
  restatement: 'Restatement',
  restatementDesc: 'Restatement Description',
  archivalDate: 'Archival Date',
  // 一整包答案的 diff 由 expandQc35AnswerChanges 攤成逐題列；這個標籤只在
  // 攤不開（形狀不對的 legacy 資料）時墊底。
  answers: 'Form QC35 Checklist',
  remarks: 'Remarks',
  reviewerName: 'Reviewer',
};

// checklist 表頭／參照列要的涵蓋案：名字一律用**申請單自己的快照**（送單當下
// 的公司名，主檔之後改名才印「(now: …)」）；年結與簽署日只有第一案有即時資料
// （detail 端點只帶 currentEngagement；其餘由後端同一把尺把關）。
const checklistEngagements = (request, currentEngagement) =>
  (request.engagements || []).map((c) => (
    currentEngagement && c.engagementId === currentEngagement.id
      ? { ...currentEngagement, engagementName: c.engagementName, entityName: c.entityName, entityNameNow: c.entityNameNow, serviceDescription: c.serviceDescription }
      : { engagementName: c.engagementName, entityName: c.entityName, entityNameNow: c.entityNameNow, serviceDescription: c.serviceDescription }
  ));

const fmtChangeValue = withCoverageFmt((field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return String(v);
});

const WorkflowQc35Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // 「Open in Management / My Records」的分流要看 engagements:edit
  // （utils/engagementLink，同 WorkflowFsDetail）——這行漏了，該頁在 176 行
  // 直接用了 hasPermission，是編譯期就會擋下的 no-undef。
  const { hasPermission } = useAuth();

  const [request, setRequest] = useState(null);
  const [batchSiblings, setBatchSiblings] = useState([]);
  const [currentEngagement, setCurrentEngagement] = useState(null);
  const [engagementAttachments, setEngagementAttachments] = useState([]);
  const [review, setReview] = useState(null);
  const [canReview, setCanReview] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canViewEngagement, setCanViewEngagement] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [engagementLock, setEngagementLock] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/workflow/qc35/${id}`),
        fetchJSON(`/api/workflow/qc35/${id}/events`),
      ]);
      setRequest(detail.request);
      setBatchSiblings(detail.batchSiblings || []);
      setCurrentEngagement(detail.currentEngagement);
      setEngagementAttachments(detail.engagementAttachments || []);
      setReview(detail.review || null);
      setCanReview(detail.canReview);
      setCanModify(detail.canModify);
      setCanDelete(detail.canDelete);
      setCanViewEngagement(detail.canViewEngagement === true);
      setEngagementLock(detail.engagementLock || null);
      setEvents(hist.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setActing(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/workflow/qc35/${id}/approve`, { method: 'POST', body: JSON.stringify({}) });
      setNotice(data.request?.status === 'approved'
        ? 'Approved — the QC35 Form is recorded and the covered engagement(s) are marked archived.'
        : 'Your approval is recorded.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const resubmit = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/workflow/qc35/${id}/resubmit`, { method: 'POST' });
      setNotice('Submission re-submitted — reviewers have been notified.');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const hasAwaitingInfo = request && request.status === 'awaiting_info';

  // 退回前的逐題答案——checklist 檢視用刪除線標出改過的題。
  const answersWhenReturned = useMemo(() => {
    const d = changesSinceReturn(events);
    return d && d.answers ? (d.answers.before || {}) : null;
  }, [events]);

  // 退件的逐項意見：能退件的人就能逐格留言，被退回的人就讀得到。
  // checklist 的題目名字住在題庫裡——併進標籤，逐題意見才叫得出題目。
  const canComment = !!request && (review?.canRejectOnly && request.status === 'requested');
  const fc = useFieldComments({
    events,
    canComment,
    returned: !!request && request.status === 'awaiting_info',
    labels: { ...CHANGE_LABELS, ...qc35AnswerLabels() },
    expandChanges: expandQc35AnswerChanges,
  });

  return (
    <RequestDetailShell
      backTo="/workflow/qc35"
      backLabel="QC35 Form"
      loading={!request}
      error={error}
      notice={notice}
    >
      {request && (
        <FieldCommentsProvider value={fc}>
          <RequestHeader
            requestNo={request.requestNo}
            typeLabel="QC35 Form"
            badge={<WorkflowStatusBadge status={request.status} />}
            submittedBy={request.requesterEmail}
            submittedAt={request.createdAt}
          />

          {/* 鎖定橫幅只在核准前有意義：QC35 核准＝把涵蓋案標成 Completed，
              核准後的單必然「涵蓋案已結束」，而終態的單也沒有動作可被鎖——
              不藏的話每張核准過的 QC35 都掛著一段無意義的琥珀說明。 */}
          <StatusActionsCard
            lock={request.status === 'approved' ? null : engagementLock}
            sides={[{
              key: 'main',
              label: null,
              status: request.status,
              note: request.statusUpdatedAt ? formatDateTime(request.statusUpdatedAt) : null,
            }]}
            busy={acting}
            events={events}
            onApprove={canReview && request.status === 'requested' ? approve : null}
            onReject={canComment ? () => setRejecting(true) : null}
            onModify={canModify && request.status !== 'approved' ? () => setModifying(true) : null}
            onResubmit={canModify && hasAwaitingInfo ? resubmit : null}
            onDelete={canDelete ? () => setConfirmingDelete(true) : null}
          >
            {request.status === 'approved' && (
              <div>
                <p className={detailLabel}>Engagement</p>
                <p className="text-sm text-green-600 mt-1">Recorded as the current QC35 Form document</p>
                {/* 核准後的 Form QC35（含合夥人核准欄）——與 QC6/7 的表單下載同一模式。 */}
                {request.answers && (
                  <a
                    href={`${API_BASE_URL}/api/workflow/qc35/${request.id}/pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <Download className="w-4 h-4" /> Download Form QC35 (PDF)
                  </a>
                )}
              </div>
            )}
          </StatusActionsCard>

          <ChangesSinceReturnCard
            events={events}
            changeLabels={{ ...CHANGE_LABELS, ...qc35AnswerLabels() }}
            fmtChangeValue={fmtChangeValue}
            expandChanges={expandQc35AnswerChanges}
          />

          <ApprovalsCard review={review} approvals={request.approvals || []} status={request.status} />

          {/* What is being reviewed comes FIRST: the submission's answers,
              checklist and documents sit above the engagement's reference
              snapshot, so the reviewer sees the thing to approve before the
              background material. */}
          <SectionCard title="Submission Details">
            <DetailGrid>
              <DetailItem label={`Engagement(s) Covered (${(request.engagements || []).length || 1})`}>
                {(request.engagements || []).length > 0
                  ? <EngagementNameList items={request.engagements} keyOf={(e, i) => e.id || i} />
                  : <EngagementName name={request.engagementName} serviceDescription={request.serviceDescription} />}
              </DetailItem>
              {/* 核准就把涵蓋案標記成這一天歸檔——審核者要看得到它。 */}
              {/* 同一頁一起送出的其他公司——各自一張單、各自審核。 */}
              {batchSiblings.length > 0 && (
                <DetailItem label="Submitted Together With">
                  <BatchSiblings siblings={batchSiblings} current={currentEngagement} basePath="/workflow/qc35" />
                </DetailItem>
              )}
              <DetailItem label="Archival Date" field="archivalDate">{fmtDate(request.archivalDate)}</DetailItem>
              <DetailItem label="Reviewer" field="reviewerName"><NowName value={request.reviewerName} now={request.reviewerNameNow} /></DetailItem>
              <DetailItem label="Remarks" field="remarks">{request.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          {/* Form QC35 checklist（May 2026 修訂版）。表頭的客戶名／年結日
              引用涵蓋案；其餘為填單人自填的答案。legacy 舊單沒有答案就不渲
              染空表。核准後可下載 PDF（StatusActionsCard 的 approved 區）。 */}
          {request.answers && (
            <SectionCard title="File Archival Checklist">
              <Qc35ChecklistView
                answers={request.answers}
                engagements={checklistEngagements(request, currentEngagement)}
                previousAnswers={answersWhenReturned}
                extra={(item) => (
                  <FieldCommentAnchor field={`answers.${item.id}`} label={item.label} className="mt-0.5" compact hoverOnly />
                )}
              />
            </SectionCard>
          )}

          <SectionCard title="Attachments">
            <AttachmentsList
              files={request.files || []}
              downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc35/${request.id}/files/${f.id}`}
              badgeFor={(f) => (f.docType === 'screenshot'
                ? ['CaseWare screenshot', 'bg-purple-50 text-purple-700']
                : ['QC35 Form', 'bg-blue-50 text-blue-700'])}
            />
            <FieldCommentAnchor field="files" label="Attachments" />
          </SectionCard>

          {/* Linked engagement: full current snapshot + its own attachments —
              reference material, so it sits below the submission itself. */}
          {currentEngagement && (
            <SectionCard
              title="Engagement Information"
              actions={(() => {
                // Editors go to Manage Data; everyone else to their own
                // read-only copy — see utils/engagementLink.
                // A named reviewer off the team has no engagement page at
                // all — no link rather than one that opens on an access wall.
                const canEdit = hasPermission('engagements:edit');
                const href = canEdit || canViewEngagement ? engagementHref(currentEngagement, canEdit) : null;
                return href ? (
                  <RefLink to={href} className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap">
                    {canEdit ? 'Open in Management' : 'Open in My Records'} <ArrowRight className="w-4 h-4" />
                  </RefLink>
                ) : null;
              })()}
            >
              <DetailGrid>
                <div className="sm:col-span-2">
                  <DetailItem label="Engagement"><EngagementName name={currentEngagement.engagementName} now={currentEngagement.engagementNameNow} serviceDescription={currentEngagement.serviceDescription} /></DetailItem>
                </div>
                <DetailItem label="Entity Name"><NowName value={currentEngagement.entityName} now={currentEngagement.entityNameNow} /></DetailItem>
                <DetailItem label="UEN"><NowName value={currentEngagement.uen} now={currentEngagement.uenNow} /></DetailItem>
                <DetailItem label="Client Group"><NowName value={currentEngagement.clientGroup} now={currentEngagement.clientGroupNow} /></DetailItem>
                <DetailItem label="Year End (FYE)">{fmtDate(currentEngagement.yearEnd)}</DetailItem>
                <DetailItem label="Status">{currentEngagement.status || '—'}</DetailItem>
                <DetailItem label="Service Provided">{currentEngagement.serviceProvided || '—'}</DetailItem>
                <DetailItem label="Partner">{currentEngagement.partner || '—'}</DetailItem>
                <DetailItem label="Director">{currentEngagement.director || '—'}</DetailItem>
                <DetailItem label="Manager">{currentEngagement.manager || '—'}</DetailItem>
                <DetailItem label="EQR">{currentEngagement.eqr || '—'}</DetailItem>
                <DetailItem label="Current Sign-Off">{fmtDate(currentEngagement.signOffDate)}</DetailItem>
                <div className="sm:col-span-2">
                  <DetailItem label="Team Members">
                    <StaffNames list={currentEngagement.teamMembers} />
                  </DetailItem>
                </div>
              </DetailGrid>

              {/* The engagement's own documents (Setup QC6/7 + Signed Financial Statements + QC35) */}
              <div className="mt-5 pt-4 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">Engagement Attachments</h3>
                <EngagementAttachments
                  attachments={engagementAttachments}
                  downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc35/${request.id}/engagement-files/${f.source}/${f.fileId}`}
                />
              </div>
            </SectionCard>
          )}

          <HistoryCard
            events={events}
            changeLabels={{ ...CHANGE_LABELS, ...qc35AnswerLabels() }}
            fmtChangeValue={fmtChangeValue}
            expandChanges={expandQc35AnswerChanges}
          />

          {rejecting && (
            <RejectModal
              requestNo={request.requestNo}
              fieldComments={fc}
              onClose={() => setRejecting(false)}
              onSubmit={async (reason, fieldComments) => {
                await fetchJSON(`/api/workflow/qc35/${request.id}/reject`, {
                  method: 'POST',
                  body: JSON.stringify({ reason, fieldComments }),
                });
                setRejecting(false);
                fc.clear();
                setNotice('Returned to the requester for changes.');
                load();
              }}
            />
          )}
          {modifying && (
            <Qc35ModifyModal
              request={request}
              events={events}
              // 歸檔日檢查要的簽署日：detail 端點只帶第一個涵蓋案的即時資料，
              // 其餘涵蓋案由後端同一把尺把關。
              engagements={currentEngagement ? [currentEngagement] : []}
              onClose={() => setModifying(false)}
              onDone={() => {
                setModifying(false);
                setNotice(hasAwaitingInfo
                  ? 'Changes saved. Press Re-submit to send the submission back for review.'
                  : 'Changes saved.');
                load();
              }}
            />
          )}
          {confirmingDelete && (
            <ConfirmDeleteModal
              title={`Delete ${request.requestNo}`}
              message="This permanently deletes the submission and its attachments. This cannot be undone."
              onClose={() => setConfirmingDelete(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/workflow/qc35/${request.id}`, { method: 'DELETE' });
                navigate('/workflow/qc35');
              }}
            />
          )}
        </FieldCommentsProvider>
      )}
    </RequestDetailShell>
  );
};

const Qc35ModifyModal = ({ request, events = [], engagements = [], onClose, onDone }) => {
  const [archivalDate, setArchivalDate] = useState(request.archivalDate || '');
  const [answers, setAnswers] = useState(request.answers || {});
  // QC6/QC7 現況（評級＋EQR）——checklist 元件抓回來，逐題必填要看它。
  const [qcContext, setQcContext] = useState(null);
  const [remarks, setRemarks] = useState(request.remarks || '');
  const [reviewerStaffId, setReviewerStaffId] = useState(
    request.reviewerStaffId ? String(request.reviewerStaffId) : ''
  );
  useClearOffRosterPick(reviewerStaffId, setReviewerStaffId, 'partner');
  const [screenshots, setScreenshots] = useState([]);
  const { removeIds, toggle, appendTo } = useRemovableFiles();
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Everything still empty, keyed to the control it belongs to — after the
  // first Save the modal marks those controls and lists them in the banner.
  // checklist 逐題必填同 New 那一面（題庫同一把尺、同一份 QC 現況）。
  const gaps = useMemo(() => ([
    ...(archivalDate ? [] : [{ key: 'archivalDate', label: 'Archival Date' }]),
    ...(reviewerStaffId ? [] : [{ key: 'reviewerName', label: 'Reviewer' }]),
    ...qc35AnswerGaps(answers, qcContext),
  ]), [archivalDate, reviewerStaffId, answers, qcContext]);
  const validation = useFormValidation(gaps);

  const submit = async () => {
    if (!validation.validate()) { setError(null); return; }
    // 歸檔日 ≥ 簽署日——New 那一面同一個 predicate，訊息才一致。
    const dateErr = archivalDateError(archivalDate, engagements);
    if (dateErr) { setError(dateErr); return; }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('archivalDate', archivalDate);
      form.set('answers', JSON.stringify(answers));
      form.set('remarks', remarks);
      if (reviewerStaffId) form.set('reviewerStaffId', reviewerStaffId);
      screenshots.forEach(f => form.append('screenshot', f));
      appendTo(form);
      await fetchJSON(`/api/workflow/qc35/${request.id}`, { method: 'PATCH', body: form });
      onDone();
    } catch (e) { setError(e.message); setSaving(false); }
  };

  return (
    <FormModal
      title={`Modify ${request.requestNo}`}
      size="lg"
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className={btnGhost}>Cancel</button>
          <button type="button" onClick={submit} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <FormValidationProvider value={validation.providerValue}>
        <ReturnCommentsBanner events={events} />
        <Field label="Archival Date" field="archivalDate" required hint={ARCHIVAL_DATE_HINT} error={archivalDateError(archivalDate, engagements)}>
          <input type="date" value={archivalDate} onChange={e => setArchivalDate(e.target.value)} className={inputCls} />
        </Field>
        {/* checklist 三面同步的第二面——表頭引用第一涵蓋案的即時資料。 */}
        <Qc35ChecklistForm
          answers={answers}
          onChange={setAnswers}
          engagements={checklistEngagements(request, engagements[0] || null)}
          onContext={setQcContext}
        />
        <div>
          <EditableAttachments
            files={request.files || []}
            removeIds={removeIds}
            onToggle={toggle}
            downloadHref={(f) => `${API_BASE_URL}/api/workflow/qc35/${request.id}/files/${f.id}`}
            label="Attachments"
          />
          <FieldCommentInline field="files" />
          {(request.files || []).length === 0 && (
            <p className="mb-2 text-xs text-slate-500">No files attached yet.</p>
          )}
        </div>
        {/* CaseWare 截圖（Form QC35 May 2026 必附）——這條流程唯一的上傳欄；
            沒有截圖的 legacy 舊單也在這裡補。 */}
        <Field label="CaseWare Audit Working Papers Screenshot" field="screenshot" hint={QC35_SCREENSHOT_HINT}>
          <FilePicker
            multiple
            accept=".png,.jpg,.jpeg,.gif,.webp"
            onChange={e => setScreenshots(Array.from(e.target.files || []))}
            files={screenshots}
          />
          <Qc35ScreenshotExample />
        </Field>
        <Field label="Remarks" field="remarks">
          <textarea rows="2" value={remarks} onChange={e => setRemarks(e.target.value)} className={inputCls} />
        </Field>
        {/* Reviewer pick — last, matching the New page's final Reviewer section. */}
        <Field label="Reviewer" required field="reviewerName"
          hint="You pick who reviews this submission, from the firm's Partners. Changing it voids recorded approvals.">
          <ReviewerSelect value={reviewerStaffId} onChange={setReviewerStaffId} grade="partner" />
        </Field>
        {(error || validation.error) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <span>{error || validation.error}</span>
          </div>
        )}
      </FormValidationProvider>
    </FormModal>
  );
};

export default WorkflowQc35Detail;
